import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NotFoundPage from './NotFoundPage';
import './DjangoFallback404.scss';

const DjangoFallback404: React.FC = () => {
  const location = useLocation();
  const [isCheckingDjango, setIsCheckingDjango] = useState(true);
  const [shouldRedirectToDjango, setShouldRedirectToDjango] = useState(false);
  const [checkProgress, setCheckProgress] = useState(0);

  useEffect(() => {
    // Skip Django check for React routes (scanner, events, etc.)
    // These routes are handled by React Router and served by Django's react_app view
    const isReactRoute = 
      location.pathname.startsWith('/scanner/') ||
      location.pathname.startsWith('/events/') ||
      location.pathname.startsWith('/addons/') ||
      location.pathname.startsWith('/profile/') ||
      location.pathname.startsWith('/chat/') ||
      location.pathname.startsWith('/cart/') ||
      location.pathname.startsWith('/transfer/') ||
      location.pathname.startsWith('/verify/') ||
      location.pathname.startsWith('/secure-pass/') ||
      location.pathname.startsWith('/payment/') ||
      location.pathname.startsWith('/checkout/') ||
      location.pathname.startsWith('/orders/checkout/');

    if (isReactRoute) {
      // This is a React route, show 404 immediately without checking Django
      setIsCheckingDjango(false);
      return;
    }

    let timeoutId: NodeJS.Timeout;
    let progressInterval: NodeJS.Timeout;
    let fetchTimeoutId: NodeJS.Timeout;
    let controller: AbortController | null = null;
    let isMounted = true;

    const checkDjangoRoute = async () => {
      try {
        // Simulate progress for better UX
        progressInterval = setInterval(() => {
          if (isMounted) {
            setCheckProgress((prev) => {
              if (prev >= 90) return prev;
              return prev + 10;
            });
          }
        }, 200);

        // Set timeout for the check (5 seconds)
        timeoutId = setTimeout(() => {
          if (isMounted) {
            setIsCheckingDjango(false);
            if (progressInterval) clearInterval(progressInterval);
          }
        }, 5000);

        // Create abort controller for fetch timeout
        controller = new AbortController();
        fetchTimeoutId = setTimeout(() => {
          if (controller) {
            controller.abort();
          }
        }, 4000);

        // Make a HEAD request to check if Django has this route
        const response = await fetch(location.pathname, {
          method: 'HEAD',
          credentials: 'include', // Include cookies for authentication
          signal: controller.signal,
        });

        clearTimeout(fetchTimeoutId);
        
        if (progressInterval) clearInterval(progressInterval);
        if (timeoutId) clearTimeout(timeoutId);
        
        if (!isMounted) return;

        setCheckProgress(100);

        // If Django returns a successful response (not 404), redirect to Django
        if (response.ok || response.status !== 404) {
          setShouldRedirectToDjango(true);
          // Small delay to show the redirect state
          setTimeout(() => {
            window.location.href = location.pathname + location.search;
          }, 500);
        } else {
          setIsCheckingDjango(false);
        }
      } catch (error: any) {
        if (progressInterval) clearInterval(progressInterval);
        if (timeoutId) clearTimeout(timeoutId);
        if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
        
        if (!isMounted) return;

        // Handle AbortError (timeout) differently
        if (error.name !== 'AbortError' && error.name !== 'TimeoutError') {
          // Network or other errors
          console.log('Django route check failed, showing React 404:', error);
        }
        setIsCheckingDjango(false);
      }
    };

    checkDjangoRoute();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (progressInterval) clearInterval(progressInterval);
      if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
      if (controller) {
        controller.abort();
      }
    };
  }, [location.pathname, location.search]);

  if (shouldRedirectToDjango) {
    return (
      <div className="django-fallback-page">
        <div className="container">
          <div className="checking-content redirecting">
            <div className="status-icon">
              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="3" fill="none" className="outer-circle"/>
                <path d="M20 32 L28 40 L44 24" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="checkmark"/>
              </svg>
            </div>
            <h2 className="status-title">Route Found!</h2>
            <p className="status-description">Redirecting you to the page...</p>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isCheckingDjango) {
    return (
      <div className="django-fallback-page">
        <div className="container">
          <div className="checking-content">
            <div className="status-icon checking">
              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="3" fill="none" className="outer-circle"/>
                <circle cx="32" cy="32" r="20" stroke="currentColor" strokeWidth="2" fill="none" className="inner-circle" strokeDasharray="31.416" strokeDashoffset="15.708">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 32 32"
                    to="360 32 32"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <h2 className="status-title">Checking Route</h2>
            <p className="status-description">
              Verifying if this page exists in the application...
            </p>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${checkProgress}%` }}></div>
            </div>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show React's 404 page
  return <NotFoundPage />;
};

export default DjangoFallback404;
